import { useState, useRef } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Shield, User, Mail, BookOpen, Phone, KeyRound, Eye, EyeOff, Camera, Trash2, Pencil, PenLine } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  const [sigV, setSigV] = useState(0);
  const [sigPreview, setSigPreview] = useState(null); // { dataUrl, file }
  const [sigSaving, setSigSaving] = useState(false);
  const fileInput = useRef(null);
  const sigInput = useRef(null);
  if (!user) return null;

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const photoUrl = user.has_photo ? `${apiBase}/profile/photo/${user.id}?v=${user.photo_v || ""}` : null;
  const sigUrl = user.has_signature ? `${apiBase}/profile/signature/${user.id}?v=${sigV}` : null;
  const subjectsLabel = (Array.isArray(user.subjects) && user.subjects.length
    ? user.subjects
    : (user.subject ? [user.subject] : [])
  ).join(", ");

  const onPickFile = () => fileInput.current?.click();
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Only JPG, PNG or WebP."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large. Max 10 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated.");
      await refresh();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await api.delete("/profile/photo");
      toast.success("Photo removed.");
      await refresh();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove");
    }
  };

  const onPickSig = () => sigInput.current?.click();
  const onSigChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Only JPG, PNG or WebP."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large. Max 10 MB."); return; }
    setSigUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Process without saving so staff can confirm how it'll print.
      const { data } = await api.post("/profile/signature/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSigPreview({ dataUrl: data.data_url, file: f });
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not read that image");
    } finally { setSigUploading(false); }
  };
  const onSigConfirm = async () => {
    if (!sigPreview?.file) return;
    setSigSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", sigPreview.file);
      await api.post("/profile/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature saved — it'll print with a clean, transparent background.");
      setSigPreview(null);
      setSigV(Date.now());
      await refresh();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Save failed");
    } finally { setSigSaving(false); }
  };
  const onSigRemove = async () => {
    if (!window.confirm("Remove your digital signature?")) return;
    try {
      await api.delete("/profile/signature");
      toast.success("Signature removed.");
      setSigV(Date.now());
      await refresh();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">My account</div>
        <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily:"Outfit"}}>My profile</h1>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="profile-card">
          <div className="p-6 flex items-center gap-4 border-b border-slate-100">
            <div className="relative">
              <button
                type="button"
                onClick={onPickFile}
                disabled={uploading}
                className="group h-20 w-20 rounded-2xl overflow-hidden bg-sky-50 text-sky-700 flex items-center justify-center text-2xl font-semibold ring-2 ring-transparent hover:ring-sky-200 transition"
                data-testid="profile-photo-btn"
              >
                {photoUrl ? (
                  <img src={photoUrl} alt={user.name} className="h-full w-full object-cover" />
                ) : (
                  <span>{user.name?.split(" ").map((p) => p[0]).slice(0,2).join("")}</span>
                )}
                <div className="absolute inset-0 rounded-2xl bg-slate-900/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                  <Camera className="h-5 w-5" />
                </div>
              </button>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFileChange} data-testid="profile-photo-input" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{user.name}</div>
              <div className="text-sm text-slate-500">{subjectsLabel || (user.role === "admin" ? "Administrator" : "Teacher")}</div>
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                <button type="button" onClick={onPickFile} disabled={uploading} className="hover:text-sky-600 inline-flex items-center gap-1" data-testid="profile-photo-change">
                  <Camera className="h-3 w-3" /> {uploading ? "Uploading…" : (user.has_photo ? "Change photo" : "Upload photo")}
                </button>
                {user.has_photo && (
                  <button type="button" onClick={onRemove} className="hover:text-rose-600 inline-flex items-center gap-1" data-testid="profile-photo-remove">
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
            </div>
            {user.role === "admin" ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
                <Shield className="h-3 w-3" /> Admin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200">
                <User className="h-3 w-3" /> Teacher
              </span>
            )}
          </div>
          <div className="p-6 grid sm:grid-cols-2 gap-5">
            <Item icon={Mail} label="Email" value={user.email} />
            <Item icon={BookOpen} label="Subject" value={subjectsLabel || "—"} />
            <Item icon={Phone} label="Phone" value={user.phone || "—"} />
            <Item icon={Shield} label="Role" value={user.role === "admin" ? "Administrator" : "Teacher"} />
          </div>

          {user.role !== "admin" && (
            <div className="px-6 pb-6 border-t border-slate-100 pt-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Assigned classes &amp; subjects</div>
              {(!user.assignments || user.assignments.length === 0) ? (
                <div className="text-sm text-rose-600" data-testid="profile-no-assignments">
                  No classes assigned yet. Please contact your admin.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="profile-assignments">
                  {user.assignments.map((a) => {
                    const subj = (Array.isArray(a.subjects) && a.subjects.length
                      ? a.subjects.join(", ")
                      : a.subject) || subjectsLabel;
                    return (
                      <span
                        key={`${a.class_name}-${a.section}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 text-xs font-medium border border-sky-100"
                      >
                        {classLabel(a.class_name)} · Section {a.section}
                        {subj ? <span className="text-sky-500">·</span> : null}
                        {subj ? <span className="font-semibold">{subj}</span> : null}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="px-6 pb-6 border-t border-slate-100 pt-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-medium text-slate-900">Your details</div>
              <div className="text-xs text-slate-500">Update your name, email, mobile number and photo.</div>
            </div>
            <Button onClick={() => setEditOpen(true)} variant="outline" className="rounded-full" data-testid="edit-profile-btn">
              <Pencil className="h-4 w-4 mr-1" /> Edit profile
            </Button>
          </div>

          <div className="px-6 pb-6 border-t border-slate-100 pt-5 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-slate-900">Security</div>
              <div className="text-xs text-slate-500">Update the password used to sign in to this account.</div>
            </div>
            <Button onClick={() => setOpen(true)} variant="outline" className="rounded-full" data-testid="change-password-btn">
              <KeyRound className="h-4 w-4 mr-1" /> Change password
            </Button>
          </div>
        </div>

        {/* Digital signature — printed on report cards & hall tickets */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6" data-testid="signature-card">
          <div className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><PenLine className="h-4 w-4 text-sky-600" /> Digital signature</div>
                <div className="text-xs text-slate-500 mt-0.5 max-w-md">
                  Upload your signature once — it prints automatically on your students' report cards and hall tickets.
                  The white background is removed for you, so a photo of a signature on plain paper works perfectly.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={onPickSig} disabled={sigUploading} className="hover:text-sky-600 text-xs text-slate-400 inline-flex items-center gap-1" data-testid="signature-upload-btn">
                  <PenLine className="h-3.5 w-3.5" /> {sigUploading ? "Reading…" : (user.has_signature ? "Change" : "Upload")}
                </button>
                {user.has_signature && (
                  <button type="button" onClick={onSigRemove} className="hover:text-rose-600 text-xs text-slate-400 inline-flex items-center gap-1" data-testid="signature-remove-btn">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
              <input ref={sigInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onSigChange} data-testid="signature-input" />
            </div>
            <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-center justify-center min-h-[96px]" data-testid="signature-preview">
              {sigUrl ? (
                <img src={sigUrl} alt="Your signature" className="max-h-24 object-contain" />
              ) : (
                <div className="text-xs text-slate-400" data-testid="signature-empty">No signature uploaded yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordDialog open={open} onOpenChange={setOpen} />
      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} user={user} refresh={refresh} />

      {/* Signature preview — confirm the cleaned, transparent result before saving */}
      <Dialog open={!!sigPreview} onOpenChange={(o) => { if (!o && !sigSaving) setSigPreview(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="signature-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenLine className="h-4 w-4 text-sky-600" /> Confirm your signature</DialogTitle>
            <DialogDescription>
              This is exactly how it'll print on report cards & hall tickets — the paper background is removed automatically.
            </DialogDescription>
          </DialogHeader>

          {/* Report-card-style strip: white paper, signature over a "Class Teacher" line */}
          <div className="rounded-xl border border-slate-300 bg-white p-5" data-testid="signature-preview-strip">
            <div className="flex flex-col items-center">
              <div className="min-h-[90px] flex items-end justify-center">
                {sigPreview?.dataUrl && (
                  <img src={sigPreview.dataUrl} alt="Signature preview" className="max-h-24 object-contain" data-testid="signature-preview-image" />
                )}
              </div>
              <div className="w-48 border-t border-slate-400 mt-1" />
              <div className="text-[11px] text-slate-500 mt-1">Class Teacher</div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">
            Tip: for the cleanest result, use a clear photo of your signature on plain paper with good lighting.
          </p>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-full" disabled={sigSaving} onClick={() => { setSigPreview(null); onPickSig(); }} data-testid="signature-preview-retake">
              Choose another
            </Button>
            <Button type="button" className="rounded-full bg-sky-600 hover:bg-sky-700" disabled={sigSaving} onClick={onSigConfirm} data-testid="signature-preview-save">
              {sigSaving ? "Saving…" : "Looks good — Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function EditProfileDialog({ open, onOpenChange, user, refresh }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setName(user.name || ""); setEmail(user.email || ""); setPhone(user.phone || ""); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    if (!/^\d{10}$/.test((phone || "").replace(/\D/g, "").slice(-10)) ) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setSaving(true);
    try {
      const phoneChanged = (phone || "").replace(/\D/g, "").slice(-10) !== (user.phone || "").replace(/\D/g, "").slice(-10);
      await api.patch("/profile/me", { name: name.trim(), email: email.trim(), phone: phone.trim() });
      if (phoneChanged) {
        toast.success("Profile updated. You'll use your new mobile number to sign in next time.", { duration: 6000 });
      } else {
        toast.success("Profile updated.");
      }
      await refresh();
      close(false);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not update profile");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>Edit profile</DialogTitle>
          <DialogDescription>Update your name, email and mobile number. Your mobile number is used to sign in.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ep-name">Full name</Label>
            <Input id="ep-name" value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-xl" data-testid="edit-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-email">Email</Label>
            <Input id="ep-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 rounded-xl" data-testid="edit-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-phone">Mobile number</Label>
            <Input id="ep-phone" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-10 rounded-xl" data-testid="edit-phone-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)} className="rounded-full" data-testid="edit-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="edit-submit">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); setShow(false); };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!cur || !next || !confirm) { toast.error("Please fill all fields"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (cur === next) { toast.error("New password must be different from the current one"); return; }
    setSaving(true);
    try {
      await api.post("/profile/password", { current_password: cur, new_password: next });
      toast.success("Password updated. Use your new password next time you sign in.");
      close(false);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>Change password</DialogTitle>
          <DialogDescription>Enter your current password and a new one (min. 6 characters).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PwField id="cur" label="Current password" value={cur} onChange={setCur} show={show} setShow={setShow} testid="pw-current" />
          <PwField id="new" label="New password" value={next} onChange={setNext} show={show} setShow={setShow} testid="pw-new" />
          <PwField id="confirm" label="Confirm new password" value={confirm} onChange={setConfirm} show={show} setShow={setShow} testid="pw-confirm" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)} className="rounded-full" data-testid="pw-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="pw-submit">
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PwField({ id, label, value, onChange, show, setShow, testid }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-xl pr-11" data-testid={testid} />
        <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function Item({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-sm text-slate-900">{value}</div>
    </div>
  );
}

function classLabel(c) {
  if (c === "LKG" || c === "UKG") return c;
  const n = parseInt(c, 10);
  if (isNaN(n)) return c;
  const s = ["th","st","nd","rd"], v = n % 100;
  return `${n}${s[(v-20)%10] || s[v] || s[0]}`;
}
